import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { business } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Pay() {
  const { token } = useParams();
  const [hold, setHold] = useState(null);
  const [error, setError] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [done, setDone] = useState(false);
  const [processing, setProcessing] = useState(false);
  const buttonsRef = useRef(null);
  const renderedRef = useRef(false);

  useEffect(() => {
    document.title = `Secure Payment Hold | ${business.name}`;
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);

    (async () => {
      try {
        const { data } = await axios.get(`${API}/pay/${token}`);
        setHold(data);
        if (data.status !== "Link Sent") return;
        const cfg = await axios.get(`${API}/paypal/config`);
        if (window.paypal) {
          renderButtons();
          return;
        }
        const existing = document.querySelector('script[data-paypal-sdk="1"]');
        if (existing) {
          existing.addEventListener("load", renderButtons, { once: true });
          return;
        }
        const s = document.createElement("script");
        s.dataset.paypalSdk = "1";
        s.src = `https://www.paypal.com/sdk/js?client-id=${cfg.data.client_id}&currency=USD&intent=authorize&disable-funding=paylater`;
        s.onload = () => renderButtons();
        s.onerror = () => setError("Could not load the payment form. Please try again later.");
        document.body.appendChild(s);
      } catch (err) {
        setError(err.response?.data?.detail || "This payment link is not valid.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const renderButtons = () => {
    if (
      renderedRef.current ||
      !window.paypal ||
      !buttonsRef.current ||
      buttonsRef.current.querySelector("iframe")
    )
      return;
    renderedRef.current = true;
    window.paypal
      .Buttons({
        createOrder: async () => {
          const { data } = await axios.post(`${API}/pay/${token}/create-order`);
          return data.order_id;
        },
        onApprove: async (d) => {
          setProcessing(true);
          try {
            await axios.post(`${API}/pay/${token}/authorize`, { order_id: d.orderID });
            setDone(true);
          } catch (err) {
            toast.error(err.response?.data?.detail || "Authorization failed. Please contact us.");
          } finally {
            setProcessing(false);
          }
        },
        onError: () => toast.error("Payment could not be completed. Please try again."),
      })
      .render(buttonsRef.current);
  };

  return (
    <div data-testid="pay-page" className="min-h-screen bg-[#1C1C1C] text-white flex items-center justify-center px-5 py-16">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-3 mb-8">
          <span className="h-9 w-9 grid place-items-center bg-[#FF4500] text-[#141414] font-display font-extrabold text-lg">D</span>
          <span className="font-display font-extrabold tracking-tight text-lg leading-none">
            DUKE SUMMERS
            <span className="block text-[10px] tracking-[0.35em] text-[#FF4500] font-bold">MOVING</span>
          </span>
        </div>

        {error ? (
          <div data-testid="pay-error" className="border border-[#3d3d3d] bg-[#262626] p-8 text-center">
            <p className="text-neutral-300 leading-relaxed">{error}</p>
            <p className="mt-4 text-sm text-neutral-500">
              Questions? Call or text {business.phone}.
            </p>
          </div>
        ) : done ? (
          <div data-testid="pay-success" className="border border-[#3d3d3d] bg-[#262626] p-8 text-center">
            <CheckCircle2 className="h-14 w-14 text-[#FF4500] mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-display font-bold uppercase tracking-tight text-3xl">Hold Placed</h1>
            <p className="mt-4 text-neutral-300 leading-relaxed">
              Thank you, {hold.customer_name.split(" ")[0]}. Your hold of{" "}
              <span className="text-white font-semibold">${Number(hold.amount).toFixed(2)}</span>{" "}
              has been authorized. You are not charged until your job is complete.
            </p>
            <p className="mt-4 text-sm text-neutral-500">
              Questions? Call or text {business.phone}.
            </p>
          </div>
        ) : !hold ? (
          <div className="border border-[#3d3d3d] bg-[#262626] p-8 text-center text-neutral-400">Loading…</div>
        ) : (
          <div className="border border-[#3d3d3d] bg-[#262626] p-6 sm:p-8">
            <p className="text-xs tracking-[0.3em] uppercase text-[#FF4500] font-bold mb-4">
              Secure Payment Hold
            </p>
            <h1 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-3xl">
              Hi {hold.customer_name.split(" ")[0]}, confirm your hold
            </h1>
            <div className="mt-6 border border-[#3d3d3d] bg-[#1C1C1C] p-5">
              <p className="text-sm text-neutral-300 leading-relaxed">{hold.job_description}</p>
              <p data-testid="pay-amount" className="mt-4 font-display font-extrabold text-4xl">
                ${Number(hold.amount).toFixed(2)}
              </p>
            </div>
            <p className="mt-5 text-sm text-neutral-400 leading-relaxed">
              This places a hold for the amount shown. You are not charged until
              your job is complete.
            </p>

            <label
              data-testid="pay-consent-label"
              className="mt-5 flex items-start gap-3 border border-[#3d3d3d] bg-[#1C1C1C] p-4 cursor-pointer hover:border-[#FF4500]/50 transition-colors"
            >
              <Checkbox
                data-testid="pay-consent-checkbox"
                checked={agreed}
                onCheckedChange={(v) => setAgreed(!!v)}
                className="mt-0.5 border-[#555] data-[state=checked]:bg-[#FF4500] data-[state=checked]:border-[#FF4500]"
              />
              <span className="text-sm text-neutral-200 leading-relaxed">
                I authorize Duke Summers Moving to hold this amount and to charge
                up to this amount when the job is complete.
              </span>
            </label>

            <div className={`mt-6 relative ${!agreed ? "opacity-40 pointer-events-none" : ""}`}>
              <div ref={buttonsRef} data-testid="paypal-buttons" />
              {processing && (
                <p className="mt-3 text-sm text-neutral-400 text-center">Processing your authorization…</p>
              )}
            </div>
            {!agreed && (
              <p className="mt-3 text-xs text-neutral-500 text-center">
                Check the authorization box above to continue.
              </p>
            )}

            <p className="mt-6 flex items-center justify-center gap-2 text-xs text-neutral-500">
              <ShieldCheck className="h-4 w-4" /> Payments processed securely by PayPal
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
